import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { BoundingBox } from "../../core/mdl/BoundingBox";
import { Interactor, InteractorType } from "../../core/tool/Interactor";
import { InteractorManager } from "../../core/tool/InteractorManager";
import { SelectionDefault } from "../../core/tool/SelectionDefault";
import { quaternionFromAxesToRef, VECTOR3_Y } from "../../core/utils/MathUtils";
import { resizeInViewSpace } from "../../core/utils/MeshUtils";
import { CameraMode } from "../../core/view/CameraControl";
import { ViewLayerMaskFlags } from "../../core/view/View";
import { getDataPath, Log } from "../../utils";
import { eq } from "../../utils/math";
import { ajax } from "../../utils/async";
import { joinPath } from "../../utils/string";
import { DefaultMove } from "./DefaultMove";
import { DefaultRotate, RotationAxis } from "./DefaultRotate";
export class Default extends SelectionDefault {
    constructor(pApp) {
        super(pApp);
        this.moveXZEnabled = true;
        this.moveYEnabled = true;
        this.moveYBelowGroundEnabled = true;
        this.groundYPosition = 0;
        this.moveLimitMin = new Vector3(-20, -20, -20);
        this.moveLimitMax = new Vector3(20, 20, 20);
        this.rotateXEnabled = true;
        this.rotateYEnabled = true;
        this.rotateZEnabled = true;
        this.automaticInteractorVisibility = true;
        this.mInteractorManager = new InteractorManager(this.mApp.viewer);
        this.mInteractorManager.enabled = false;
        this.mMoveTool = new DefaultMove(this.mApp);
        this.mRotateTool = new DefaultRotate(this.mApp);
        this.createInteractors();
    }
    start(pParameter) {
        this.mInteractorManager.enabled = true;
        return (super.start(pParameter));
    }
    stop() {
        this.mRotateInteractorX.interactorData.lineNode.setEnabled(false);
        this.mRotateInteractorY.interactorData.lineNode.setEnabled(false);
        this.mRotateInteractorZ.interactorData.lineNode.setEnabled(false);
        this.mInteractorManager.enabled = false;
        this.mApp.viewer.requestRenderFrame();
        super.stop();
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    updateInteractors() {
        this.updateRotateInteractors();
        this.updateMoveInteractorY();
    }
    startPointerMoveTool(pEvent, pState) {
        if (pEvent.ctrlKey || !pState.isPrimaryPointer(pEvent))
            return;
        //start move tool if picked top level element is currently selected
        if (this.moveXZEnabled) {
            let tIsPickedElementSelected = false;
            if (this.mPickedElement != null) {
                const tTopLevelElement = this.mPickedElement.getTopLevelElement();
                tIsPickedElementSelected = tTopLevelElement.isSelected;
            }
            if (tIsPickedElementSelected && this.mSelectionInfo.canTranslate) {
                this.mMoveTool.initialize(this.mPickedPos, pEvent.pointerId, false, this.moveYBelowGroundEnabled, this.groundYPosition);
                this.mMoveTool.setMoveLimit(this.moveLimitMin, this.moveLimitMax);
                this.mApp.tools.startTool(this.mMoveTool);
            }
        }
    }
    createInteractors() {
        //load svg and setup theme color
        const tPromise = ajax("GET", joinPath(getDataPath(), "pl/images/sprite.svg"), undefined, { responseType: "text" }).then((pXml) => {
            const tXml = pXml.replace(/#95C11F/g, this.mApp.theme.mainColorBright);
            return ("data:image/svg+xml;base64," + btoa(tXml));
        });
        //rotate interactors
        this.mRotateInteractorX = this.createRotateInteractor(RotationAxis.X, tPromise);
        this.mRotateInteractorY = this.createRotateInteractor(RotationAxis.Y, tPromise);
        this.mRotateInteractorZ = this.createRotateInteractor(RotationAxis.Z, tPromise);
        /// move interactor
        const tInteractorData = {};
        this.mMoveInteractorY = this.createInteractor("wcf-pl movey-interactor", "scale(0.75, 0.75)", tPromise);
        this.mMoveInteractorY.interactorData = tInteractorData;
        this.mMoveInteractorY.onTriggerAction = () => {
            this.mMoveTool.initialize(tInteractorData.refPoint, null, true, this.moveYBelowGroundEnabled, this.groundYPosition);
            this.mMoveTool.setMoveLimit(this.moveLimitMin, this.moveLimitMax);
            this.mApp.tools.startTool(this.mMoveTool);
        };
    }
    createInteractor(pCssClass, pTransform, pSvgPromise) {
        const tDiv = document.createElement("div");
        const tInnerDiv = document.createElement("div");
        const tSpan = document.createElement("span");
        tDiv.className = pCssClass;
        tInnerDiv.appendChild(tSpan);
        tInnerDiv.style.transform = pTransform;
        tDiv.appendChild(tInnerDiv);
        const tInteractor = new Interactor(tDiv, this.mApp.viewer);
        tInteractor.type = InteractorType.Dragger;
        tInteractor.transform3d = true;
        tInteractor.transform3dRotation = new Quaternion;
        this.mInteractorManager.addInteractor(tInteractor);
        //assign svg
        pSvgPromise.then((pUrl) => {
            tSpan.style.backgroundImage = "url('" + pUrl + "')";
        }).catch((pError) => {
            Log.error("Failed to load interactor sprite SVG.", pError);
        });
        return (tInteractor);
    }
    createRotateInteractor(pAxis, pSvgPromise) {
        const tScene = this.mApp.model.scene;
        const tInteractor = this.createInteractor("wcf-pl rotate-interactor", "rotate(-135deg) scale(0.75, 0.75)", pSvgPromise);
        const tInteractorData = {};
        tInteractor.interactorData = tInteractorData;
        tInteractor.onTriggerAction = () => {
            this.mRotateTool.initialize(tInteractorData.refPoint, tInteractorData.startPoint, pAxis);
            this.mApp.tools.startTool(this.mRotateTool);
        };
        //line node
        const tDistance = Default.ROTATE_INTERACTOR_DISTANCE;
        const tNode = new TransformNode("", tScene);
        tNode.setEnabled(false);
        tNode.rotationQuaternion = new Quaternion();
        tInteractorData.lineNode = tNode;
        //bounding box connection lines
        //single selection
        let tLine = CreateLineSystem("", {
            lines: [
                [new Vector3(-0.1 + tDistance, 0, 0), new Vector3(-0.2 + tDistance, 0, 0)]
            ],
            updatable: false,
            instance: null,
            useVertexAlpha: false
        }, tScene);
        tLine.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        tLine.isPickable = false;
        tLine.color = Color3.FromHexString("#868686");
        tLine.renderingGroupId = 1;
        tLine.position.x = 0.2 + tDistance;
        tLine.parent = tNode;
        tInteractorData.singleSelectionLine = tLine;
        //multi selection
        tLine = CreateLineSystem("", {
            lines: [
                [new Vector3(-0.1 + tDistance, 0, 0), new Vector3(-0.2 + tDistance, 0, 0)],
                [new Vector3(-0.2 + tDistance, -0.2, 0), new Vector3(-0.2 + tDistance, 0.2, 0)]
            ],
            updatable: false,
            instance: null,
            useVertexAlpha: false
        }, tScene);
        tLine.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        tLine.isPickable = false;
        tLine.color = Color3.FromHexString("#868686");
        tLine.renderingGroupId = 1;
        tLine.position.x = 0.2 + tDistance;
        tLine.parent = tNode;
        tInteractorData.multiSelectionLine = tLine;
        return (tInteractor);
    }
    updateRotateInteractors() {
        if (this.mSelection.length === 0 || !this.mSelectionInfo.canRotate) {
            this.mRotateInteractorX.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorY.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorZ.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorX.visible = false;
            this.mRotateInteractorY.visible = false;
            this.mRotateInteractorZ.visible = false;
            return;
        }
        //rotate X
        if (this.rotateXEnabled) {
            this.updateRotateInteractor(this.mRotateInteractorX, BoundingBox.SIDE_MIN_X, BoundingBox.SIDE_MAX_X, this.mSelectionInfo.worldPoints);
        }
        else {
            this.mRotateInteractorX.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorX.visible = false;
        }
        //rotate Y
        if (this.rotateYEnabled) {
            this.updateRotateInteractor(this.mRotateInteractorY, BoundingBox.SIDE_MIN_Y, BoundingBox.SIDE_MAX_Y, this.mSelectionInfo.worldPoints);
        }
        else {
            this.mRotateInteractorY.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorY.visible = false;
        }
        //rotate Z
        if (this.rotateZEnabled) {
            this.updateRotateInteractor(this.mRotateInteractorZ, BoundingBox.SIDE_MIN_Z, BoundingBox.SIDE_MAX_Z, this.mSelectionInfo.worldPoints);
        }
        else {
            this.mRotateInteractorZ.interactorData.lineNode.setEnabled(false);
            this.mRotateInteractorZ.visible = false;
        }
    }
    updateRotateInteractor(pInteractor, pBoxSideMin, pBoxSideMax, pBoxPoints) {
        const tInteractorData = pInteractor.interactorData;
        const tNode = tInteractorData.lineNode;
        tNode.setEnabled(true);
        pInteractor.visible = true;
        const tBoxCenter = Vector3.Center(pBoxPoints[0], pBoxPoints[5]);
        let tCamPosition = this.mView.camera.position;
        if (this.mView.cameraControl.cameraMode !== CameraMode.Perspective) {
            const tBoxSize = pBoxPoints[5].subtract(pBoxPoints[0]).length();
            const tDir = this.mView.cameraControl.direction.negate();
            tDir.scaleInPlace(tBoxSize * 2);
            tCamPosition = tBoxCenter.add(tDir);
        }
        //get side which is further away from camera
        const tCenterMin = Vector3.Center(pBoxPoints[pBoxSideMin[0]], pBoxPoints[pBoxSideMin[2]]);
        const tCenterMax = Vector3.Center(pBoxPoints[pBoxSideMax[0]], pBoxPoints[pBoxSideMax[2]]);
        const tCameraDistMin = tCenterMin.subtract(tCamPosition).length();
        const tCameraDistMax = tCenterMax.subtract(tCamPosition).length();
        let tSideCenter = tCenterMin;
        let tSide = pBoxSideMin;
        if (tCameraDistMax > tCameraDistMin) {
            tSideCenter = tCenterMax;
            tSide = pBoxSideMax;
        }
        //get edge which is the closest to the camera
        const tEdges = new Array();
        tEdges.push(Vector3.Center(pBoxPoints[tSide[3]], pBoxPoints[tSide[0]]));
        tEdges.push(Vector3.Center(pBoxPoints[tSide[2]], pBoxPoints[tSide[3]]));
        tEdges.push(Vector3.Center(pBoxPoints[tSide[1]], pBoxPoints[tSide[2]]));
        tEdges.push(Vector3.Center(pBoxPoints[tSide[0]], pBoxPoints[tSide[1]]));
        let tFoundEdgeCenter = null;
        let tDistance = 0;
        for (let i = 0; i < 4; ++i) {
            const tD = tEdges[i].subtract(tCamPosition).length();
            //prefer first found edge, this is necessary for orthographic views to prevent switching interactor positions
            if ((tD < tDistance && !eq(tD, tDistance)) || tFoundEdgeCenter == null) {
                tFoundEdgeCenter = tEdges[i];
                tDistance = tD;
            }
        }
        const tEdgeCenter = tFoundEdgeCenter;
        //build coordinate system for correct interactor rotation
        const tAxisX = tEdgeCenter.subtract(tSideCenter).normalize();
        const tAxisZ = tBoxCenter.subtract(tSideCenter).normalize();
        const tAxisY = Vector3.Cross(tAxisZ, tAxisX);
        tNode.position = tEdgeCenter.clone();
        tNode.position.addInPlace(tAxisX.scale(Default.ROTATE_INTERACTOR_OFFSET)); //add position offset
        tNode.position.addInPlace(tAxisZ.scale(-Default.ROTATE_INTERACTOR_OFFSET)); //add position offset
        quaternionFromAxesToRef(tAxisX, tAxisY, tAxisZ, tNode.rotationQuaternion);
        tInteractorData.refPoint = tSideCenter;
        tInteractorData.startPoint = tEdgeCenter;
        tInteractorData.singleSelectionLine.isVisible = this.mSelection.length === 1;
        tInteractorData.multiSelectionLine.isVisible = this.mSelection.length > 1;
        //get vector pointing from interactor to camera
        const tCameraVec = this.getDirectionToCamera(tNode.position, this.mView);
        //hide interactor if it not useful with the current camera direction
        const tDot = Vector3.Dot(tAxisX, tCameraVec);
        if (this.automaticInteractorVisibility && Math.abs(tDot) > 0.9) {
            tNode.setEnabled(false);
            pInteractor.visible = false;
        }
        else {
            pInteractor.position = tNode.position.clone();
            pInteractor.transform3dRotation = tNode.rotationQuaternion.clone();
            pInteractor.updateViewPosition();
            resizeInViewSpace(this.mView, tNode, 0.1, 15.0);
        }
    }
    updateMoveInteractorY() {
        if (this.mSelection.length === 0 || !this.mSelectionInfo.canTranslate || !this.moveYEnabled) {
            this.mMoveInteractorY.visible = false;
            return;
        }
        const tIsMousePointer = this.mApp.viewer.inputState.pointerType === "mouse";
        const tMode = this.mView.cameraControl.cameraMode;
        if (tIsMousePointer) {
            //show only in orthographic and perspective views
            if (tMode != CameraMode.Orthographic && tMode != CameraMode.Perspective) {
                this.mMoveInteractorY.visible = false;
                return;
            }
        }
        else {
            //show in all views except top and bottom
            if (tMode === CameraMode.Top || tMode === CameraMode.Bottom) {
                this.mMoveInteractorY.visible = false;
                return;
            }
        }
        const tBounds = this.mSelectionInfo.worldBounds;
        const tCenter = tBounds.getCenter();
        this.mMoveInteractorY.position = tCenter;
        this.mMoveInteractorY.position.y = tBounds.max.y + 0.025;
        const tInteractorData = this.mMoveInteractorY.interactorData;
        tInteractorData.refPoint = tCenter;
        tInteractorData.refPoint.y = tBounds.max.y;
        //get vector pointing from interactor to camera
        const tCameraVec = this.getDirectionToCamera(this.mMoveInteractorY.position, this.mView);
        //hide interactor if camera is looking down or up considerably
        const tDot = Vector3.Dot(VECTOR3_Y, tCameraVec);
        if (this.automaticInteractorVisibility && Math.abs(tDot) > 0.9) {
            this.mMoveInteractorY.visible = false;
        }
        else {
            //build coordinate system for correct interactor rotation
            const tAxisY = VECTOR3_Y;
            const tAxisZ = tCameraVec;
            const tAxisX = Vector3.Cross(tAxisZ, tAxisY);
            tAxisX.normalize();
            Vector3.CrossToRef(tAxisX, tAxisY, tAxisZ);
            quaternionFromAxesToRef(tAxisX, tAxisY, tAxisZ, this.mMoveInteractorY.transform3dRotation);
            this.mMoveInteractorY.visible = true;
            this.mMoveInteractorY.updateViewPosition();
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Default.ROTATE_INTERACTOR_DISTANCE = 0.0;
Default.ROTATE_INTERACTOR_OFFSET = 0.0125;
